import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { EntitiesContext, UserContext } from "../../App";
import { useGetAccountOrders } from "../../hooks/useAccount";

// Landing page for a signed-in consignor: totals for the open sale, how the
// portal works, and the key dates. Laid out in two columns so it fits a
// laptop screen without scrolling.
export default function Landing({ items }) {
  const entities = useContext(EntitiesContext);
  const user = useContext(UserContext);
  // Sold totals need the consignor's paid orders; nothing else on this page
  // loads them.
  const accountOrders = useGetAccountOrders(user.user);
  const orders = entities.state.get("order");

  const total = (inv) => inv.reduce((acc, cur) => acc + parseInt(cur.price), 0);
  const isSold = (i) => i.order(orders)?.status === "paid";

  const nonDonated = items.filter((i) => !i.donate);
  const donated = items.filter((i) => i.donate);

  // Only the totals depend on the orders request; the rest of the page is
  // static and renders regardless of how that request goes.
  const totals = () => {
    if (accountOrders.isLoading) {
      return (
        <div className="level-item has-text-centered">
          <p className="has-text-grey">Loading your totals…</p>
        </div>
      );
    }

    if (accountOrders.isError) {
      return (
        <div className="level-item has-text-centered">
          <p className="has-text-grey">
            Your totals are unavailable right now. Refresh the page to try again.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="level-item has-text-centered">
          <div>
            <p className="heading">Total Non Donated Items/Sold*</p>
            <p className="title">
              ${total(nonDonated)}/${total(nonDonated.filter(isSold))}
            </p>
          </div>
        </div>
        <div className="level-item has-text-centered">
          <div>
            <p className="heading">Total Donated/Sold</p>
            <p className="title">
              ${total(donated)}/${total(donated.filter(isSold))}
            </p>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="landing">
      <section className="level" aria-label="Consignment totals">
        {totals()}
      </section>

      <div className="columns is-variable is-5">
        <div className="column is-flex is-flex-direction-column is-justify-content-center">
          <h2 className="title is-5 mb-3">
            Welcome to the Waitsfield Elementary Ski &amp; Skate Sale Consignment
            Dashboard!
          </h2>
          <p className="has-text-weight-semibold mb-1">How it works:</p>
          <ol className="how-it-works mb-3" aria-label="How it works">
            <li>
              Add every item you want to sell on the{" "}
              <Link to="/dashboard/inventory/add">Add Inventory</Link> tab.
            </li>
            <li>
              Check/update items you have registered on the{" "}
              <Link to="/dashboard/inventory">Current Inventory</Link> tab.
            </li>
          </ol>
          <p className="is-size-7 has-text-grey">
            * Please note, the PTA takes 20% of the sold amount.
          </p>
        </div>

        <section className="column" aria-label="Key dates">
          <div className="box py-4">
            <p className="has-text-weight-bold has-text-centered is-size-5">
              Waitsfield Ski &amp; Skate Sale 2026
            </p>
            <p className="has-text-centered mb-3">11/14 • 9:00am–4:00pm</p>
            <ul className="key-dates">
              <li>
                <strong>Register ALL Consignment Items by 11/09 at 11pm</strong>
              </li>
              <li>
                Pick up Tags for your items at Waitsfield Elementary School from
                Wednesday to Friday <strong>(11/11-13)</strong>
              </li>
              <li>
                Drop off all Pre-registered Consignment items at Waitsfield
                Elementary on <strong>Friday 11/13 from 4pm to 7pm</strong>
              </li>
              <li>
                Pick up any Unsold Consignment Items on{" "}
                <strong>11/14 between 3:45 and 4:15pm</strong>.{" "}
                <strong>This is the ONLY opportunity to pick up unsold gear.</strong>{" "}
                Any unsold items not picked up by 4:15 on Saturday 11/14 will NOT
                be retrievable.
              </li>
            </ul>
          </div>
        </section>
      </div>

      <p className="has-text-centered">
        <Link to="/dashboard/faqs" className="button is-link is-light">
          FAQs
        </Link>
      </p>
    </div>
  );
}
